import { useState } from 'react';
import type { PlumaManagedModalProps } from '@customerio/pluma-components/react';
import {
  Box,
  Button,
  Checkbox,
  Form,
  Heading,
  Modal,
  ModalBody,
  ModalFooter,
  Paragraph,
  Stack,
  Strong,
  TextArea,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useStore } from '@nanostores/react';
import { useSubmitFeedback } from '@/features/customer-feedback';
import { authStore } from '@/services/auth/store';
import { isEmptyString } from '@/utils/string';

const FORM_ID = 'merge-feedback-form';

type FeedbackOption = 'manual' | 'last' | 'first' | 'other';

type OptionDefinition = {
  value: FeedbackOption;
  /** The plain wording sent with the feedback. */
  label: string;
  /** The wording shown, when it differs by emphasis. */
  display?: React.ReactNode;
};

const FEEDBACK_OPTIONS: readonly OptionDefinition[] = [
  { value: 'manual', label: 'Manually select a winner for each attribute' },
  {
    value: 'last',
    label: 'Whichever attribute value was set last wins',
    display: (
      <>
        Whichever attribute value was set <Strong>last</Strong> wins
      </>
    ),
  },
  {
    value: 'first',
    label: 'Whichever attribute value was set first wins',
    display: (
      <>
        Whichever attribute value was set <Strong>first</Strong> wins
      </>
    ),
  },
  { value: 'other', label: 'Other' },
];

type Choices = Record<FeedbackOption, boolean>;

const NO_CHOICES = { manual: false, last: false, first: false, other: false } satisfies Choices;

/** Why Send is disabled, or null when the answer can be sent. */
export function feedbackBlocker(choices: Choices, otherDetail: string): string | null {
  if (Object.values(choices).every((chosen) => !chosen)) {
    return 'Select at least one option';
  }
  if (choices.other && isEmptyString(otherDetail)) {
    return 'Provide feedback for "Other"';
  }
  return null;
}

/** The chosen options as the feedback body, one per line, with the free-text answer under "Other". */
export function feedbackPraise(choices: Choices, otherDetail: string): string {
  return FEEDBACK_OPTIONS.filter((option) => choices[option.value])
    .map((option) => (option.value === 'other' ? `Other: ${otherDetail.trim()}` : option.label))
    .join('\r\n');
}

type MergeFeedbackModalData = {
  /** The workspace's plural for people. */
  peoplePlural: string;
};

/**
 * "Would you like other merging options?" — asks which merge strategies the
 * person wants, sends the answer as product feedback, and thanks them.
 */
export function MergeFeedbackModal({ data, onClose, modalState }: PlumaManagedModalProps<MergeFeedbackModalData>) {
  const auth = useStore(authStore);
  const submitFeedback = useSubmitFeedback({ withSuccessSnackbar: false });
  const [choices, setChoices] = useState<Choices>(NO_CHOICES);
  const [otherDetail, setOtherDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const blocker = feedbackBlocker(choices, otherDetail);
  const title = isSubmitted ? undefined : `Tell us more about how you want to merge ${data.peoplePlural}`;

  const onSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (blocker != null || isSubmitting) return;
    setIsSubmitting(true);
    try {
      await submitFeedback({
        source: 'Merge Feedback',
        criticism: '',
        praise: feedbackPraise(choices, otherDetail),
        replyTo: auth.user?.email ?? '',
      });
      setIsSubmitted(true);
    } catch {
      // The submit hook has already reported the failure.
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <Modal state={modalState} onClose={onClose} title="Thank you" size="md" data-test="merge-feedback-modal">
        <ModalBody textAlign="center" pb="600" pt="0">
          <Heading level="3" color="bold" mb="100">
            Thank you
          </Heading>
          <Paragraph>We read all feedback, though it may take a few days.</Paragraph>
        </ModalBody>
      </Modal>
    );
  }

  return (
    <Modal state={modalState} onClose={onClose} title={title} size="md" data-test="merge-feedback-modal">
      <ModalBody>
        <Form id={FORM_ID} onSubmit={onSubmit}>
          <Paragraph weight="medium" mb="100">
            How would you like to merge {data.peoplePlural}?
          </Paragraph>
          <Paragraph mb="050">Select all that apply:</Paragraph>
          <Stack as="fieldset" gap="050">
            {FEEDBACK_OPTIONS.map((option) => (
              <Checkbox
                key={option.value}
                name={option.value}
                value={option.value}
                isChecked={choices[option.value]}
                label={option.display ?? option.label}
                onCheckedChange={(checked) => setChoices((current) => ({ ...current, [option.value]: checked }))}
              />
            ))}
            {choices.other ? (
              <Box ml="250">
                <TextArea
                  ariaLabel={`Other ${data.peoplePlural} merge feedback`}
                  placeholder="Your answer"
                  value={otherDetail}
                  onInput={(event) => setOtherDetail(event.target.value)}
                  autoFocus
                  minRows={2}
                />
              </Box>
            ) : null}
          </Stack>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Tooltip content={blocker ?? ''} isDisabled={blocker == null} isCustom>
          {(context) => (
            <Button
              ref={context.reference.setReference}
              {...context.reference.getReferenceProps()}
              type="submit"
              form={FORM_ID}
              variant="primary"
              isDisabled={blocker != null}
              isLoading={isSubmitting}
            >
              Send
            </Button>
          )}
        </Tooltip>
      </ModalFooter>
    </Modal>
  );
}
